/**
 * CRM lead contracts. Destination: `@renote/contracts`.
 *
 * Shared primitives (`uuid`, `trimmedText`, pagination, filters) live in
 * ./common — this file only owns lead-specific shapes.
 */

import { z } from 'zod'
import {
  arrayParam,
  boolParam,
  jsonParam,
  viewFilter,
  instant,
  optionalEmail,
  optionalUrl,
  pageOf,
  pageQuery,
  sortDirection,
  trimmedText,
  uuid,
} from './common'

export const leadStatusColor = z.enum([
  'gray',
  'blue',
  'green',
  'orange',
  'red',
  'violet',
  'teal',
])
export type LeadStatusColor = z.infer<typeof leadStatusColor>

export const leadStatus = z.object({
  id: uuid,
  name: z.string(),
  color: leadStatusColor,
  position: z.number().int(),
  isDefault: z.boolean(),
  isTerminal: z.boolean(),
})
export type LeadStatus = z.infer<typeof leadStatus>

/** Customer segment — an attribute, never a pipeline stage. */
export const segment = z.enum(['b2b', 'b2c', 'd2c'])
export type Segment = z.infer<typeof segment>

/** Why a scheduled demo did not happen. */
export const demoMissReason = z.enum(['postponed', 'cancelled', 'no_show'])
export type DemoMissReason = z.infer<typeof demoMissReason>

/** Server representation. Mirrors crm_lead one-for-one. */
export const lead = z.object({
  id: uuid,

  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  jobTitle: z.string().nullable(),

  organizationName: z.string().nullable(),
  website: z.string().nullable(),
  industryId: uuid.nullable(),
  noOfEmployees: z.number().int().nonnegative().nullable(),

  statusId: uuid,
  sourceId: uuid.nullable(),
  ownerId: uuid.nullable(),
  segment: segment.nullable(),

  nextFollowUpAt: instant.nullable(),
  followUpCount: z.number().int().nonnegative(),
  demoMissedReason: demoMissReason.nullable(),
  lostReasonId: uuid.nullable(),

  convertedAt: instant.nullable(),
  convertedDealId: uuid.nullable(),

  createdBy: uuid,
  createdAt: instant,
  updatedAt: instant,
})
export type Lead = z.infer<typeof lead>

/**
 * Mirrors the crm_lead_has_a_name check constraint. Duplicated deliberately: the
 * database owns the invariant, this exists so the form can report it as a field
 * error instead of surfacing a 500.
 */
const hasSomeIdentity = (v: {
  firstName: string | null
  lastName: string | null
  organizationName: string | null
  email: string | null
}) =>
  [v.firstName, v.lastName, v.organizationName, v.email].some(
    (field) => field !== null,
  )

export const createLeadInput = z
  .object({
    firstName: trimmedText(140),
    lastName: trimmedText(140),
    email: optionalEmail,
    phone: trimmedText(40),
    jobTitle: trimmedText(140),

    organizationName: trimmedText(200),
    website: optionalUrl,
    industryId: uuid.nullable(),
    noOfEmployees: z.number().int().nonnegative().nullable(),

    statusId: uuid.optional(), // defaults to crm_lead_status.is_default
    sourceId: uuid.nullable(),
    ownerId: uuid.nullable(),
    segment: segment.nullable(),

    nextFollowUpAt: instant.nullable(),
    demoMissedReason: demoMissReason.nullable(),
    // Required by the database when the status is terminal; the service turns
    // that constraint into a field error rather than a 500.
    lostReasonId: uuid.nullable(),
  })
  .strict()
  .refine(hasSomeIdentity, {
    message: 'Provide at least a name, organization, or email',
    path: ['firstName'],
  })
export type CreateLeadInput = z.infer<typeof createLeadInput>

/**
 * Partial update. Every key is optional; a key present with `null` clears the
 * column. Conversion fields are absent by design — conversion is its own
 * endpoint (see convertLeadInput in ./deal) so it can create the organization,
 * contact, deal and lead stamp in one UnitOfWork.
 */
const IDENTITY_KEYS = ['firstName', 'lastName', 'organizationName', 'email'] as const

export const updateLeadInput = createLeadInput
  .innerType()
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
  /**
   * Mirrors crm_lead_has_a_name, but only when the payload carries every
   * identity field — which is what an edit form sends. A narrower patch cannot
   * be judged here (the missing fields may still hold values), so the database
   * remains the backstop.
   */
  .refine(
    (v) => {
      const complete = IDENTITY_KEYS.every((key) => key in v)
      if (!complete) return true
      return IDENTITY_KEYS.some((key) => v[key] !== null)
    },
    { message: 'Provide at least a name, organization, or email', path: ['firstName'] },
  )
export type UpdateLeadInput = z.infer<typeof updateLeadInput>

/**
 * Schedule the next chase. Its own endpoint because it also increments
 * followUpCount, which update() deliberately cannot touch.
 */
export const scheduleFollowUpInput = z.object({ at: instant }).strict()
export type ScheduleFollowUpInput = z.infer<typeof scheduleFollowUpInput>

export const leadSortField = z.enum([
  'updatedAt',
  'createdAt',
  'firstName',
  'organizationName',
  'statusId',
  'nextFollowUpAt',
])
export type LeadSortField = z.infer<typeof leadSortField>

export const leadListQuery = pageQuery
  .extend({
    search: z.string().trim().max(200).optional(),
    statusId: arrayParam(uuid).optional(),
    sourceId: arrayParam(uuid).optional(),
    ownerId: arrayParam(uuid).optional(),
    segment: arrayParam(segment).optional(),
    /**
     * Ad-hoc filters from the view builder, validated against the entity's field
     * registry server-side before compilation. The named facets above stay for
     * the common cases and for hand-written links.
     */
    filters: jsonParam(z.array(viewFilter)).optional(),
    /**
     * The follow-up queue. Replaces filtering by six Follow Up statuses with a
     * range scan on next_follow_up_at — and unlike the statuses, it knows which
     * leads are actually overdue.
     */
    followUpDue: boolParam.default(false),
    converted: boolParam.default(false),
    sort: leadSortField.default('updatedAt'),
    direction: sortDirection.default('desc'),
  })
  .strict()
export type LeadListQuery = z.infer<typeof leadListQuery>

export const leadListResult = pageOf(lead)
export type LeadListResult = z.infer<typeof leadListResult>
